import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { IconShoppingCart, IconDownload } from "@tabler/icons-react";
import { Heading, Text } from "../../../../components/common/typography";
import { SearchBar } from "../../../../components/common/input";
import { Select } from "../../../../components/forms/select";
import { SegmentedControl } from "../../../../components/common/segmented-control";
import { StatusChip, type OrderStatus } from "../../../../components/common/chip";
import { SimpleTable, type SimpleTableColumn } from "../../../../components/common/simple-table";
import { EmptyState } from "../../../../components/common/empty-state";
import { ShimmerLoader, Shimmer } from "../../../../components/common/shimmer";
import { Pagination } from "../../../../components/common/pagination";
import { AccentStatCard } from "../../../../components/common/accent-stat-card";
import { Button } from "../../../../components/common/button";
import { AppRoutes } from "../../../../common/routes/app_routes";
import { type OrdersFilter } from "../../../sales/hooks/useOrders";
import { useSales } from "../../../sales/hooks/useSales";
import { deriveOrderStatus, formatLongDate } from "../../../sales/utils/order-status";
import { useAdminSalesStats } from "../../hooks/useAdminSalesStats";
import { salesService } from "../../../api_client";
import { showErrorToast } from "../../../../utils/toasts";
import type { SaleDto, SalesQueryDto } from "../../../../api";
import { SalesQueryDtoStateEnum, SalesQueryDtoSortOrderEnum } from "../../../../api";


type FilterTab = OrdersFilter | "canceled" | "unrouted";

const FILTER_TABS: { value: FilterTab; label: string }[] = [
  { value: "all", label: "All" },
  { value: "delivered", label: "Delivered" },
  { value: "inTransit", label: "In Transit" },
  { value: "processing", label: "Processing" },
  { value: "canceled", label: "Canceled" },
  { value: "unrouted", label: "Unrouted" },
];

const SORT_OPTIONS = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
  { value: "amount_desc", label: "Amount: High to Low" },
  { value: "amount_asc", label: "Amount: Low to High" },
];

const VALID_TABS = new Set<FilterTab>(FILTER_TABS.map((t) => t.value));
const PAGE_SIZE = 10;
const STATE_CONFIRMED = "confirmed" as unknown as SalesQueryDtoStateEnum;

function statusMatchesTab(tab: FilterTab, status: ReturnType<typeof deriveOrderStatus>): boolean {
  if (tab === "all" || tab === "unrouted") return true;
  if (tab === "canceled") return status === "cancelled";
  return status === tab;
}

function tabToQuery(tab: FilterTab): Pick<SalesQueryDto, "state" | "inDelivery" | "unrouted"> {
  if (tab === "inTransit") return { state: STATE_CONFIRMED, inDelivery: true };
  if (tab === "processing") return { state: SalesQueryDtoStateEnum.Sale };
  if (tab === "canceled") return { state: SalesQueryDtoStateEnum.Cancel };
  if (tab === "unrouted") return { state: STATE_CONFIRMED, unrouted: true };
  return { state: STATE_CONFIRMED };
}

function sortToQuery(sort: string): Pick<SalesQueryDto, "sortBy" | "sortOrder"> {
  if (sort === "oldest") return { sortBy: "date_order", sortOrder: SalesQueryDtoSortOrderEnum.Asc };
  if (sort === "amount_desc") return { sortBy: "amount_total", sortOrder: SalesQueryDtoSortOrderEnum.Desc };
  if (sort === "amount_asc") return { sortBy: "amount_total", sortOrder: SalesQueryDtoSortOrderEnum.Asc };
  return { sortBy: "date_order", sortOrder: SalesQueryDtoSortOrderEnum.Desc };
}

function ActionChip({ status }: { status: OrderStatus }) {
  if (status === "delivered") {
    return (
      <span className="inline-flex items-center rounded-xl bg-accentYellowTint px-3 py-1">
        <Text variant="bodyXsBold" tone="amber" inline>Awaiting Validation</Text>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-xl bg-bgLight px-3 py-1">
      <Text variant="bodyXsBold" tone="muted" inline>Not needed</Text>
    </span>
  );
}

const COLUMNS: SimpleTableColumn<SaleDto>[] = [
  {
    key: "name",
    label: "Orders",
    className: "flex-[2] min-w-0",
    render: (row) => <Text variant="body" tone="dark">{row.name}</Text>,
  },
  {
    key: "amount",
    label: "Amount (QAR)",
    className: "flex-1",
    render: (row) => (
      <Text variant="bodyBold" tone="dark">
        {row.amountTotal != null
          ? row.amountTotal.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })
          : "—"}
      </Text>
    ),
  },
  {
    key: "date",
    label: "Order Date",
    className: "flex-1",
    render: (row) => <Text variant="body" tone="body">{formatLongDate(row.dateOrder)}</Text>,
  },
  {
    key: "status",
    label: "Delivery Status",
    className: "flex-1",
    render: (row) => <StatusChip status={deriveOrderStatus(row) as OrderStatus} />,
  },
  {
    key: "routing",
    label: "Routing",
    className: "flex-1 flex justify-center",
    render: (row) =>
      row.routed === false ? (
        <span className="inline-flex items-center rounded-xl bg-accentYellowTint px-3 py-1">
          <Text variant="bodyXsBold" tone="amber" inline>Unrouted</Text>
        </span>
      ) : (
        <Text variant="bodyXsBold" tone="muted" inline>—</Text>
      ),
  },
  {
    key: "action",
    label: "Action",
    className: "flex-1 flex justify-end",
    render: (row) => <ActionChip status={deriveOrderStatus(row) as OrderStatus} />,
  },
];

export function AdminOrdersPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isExporting, setIsExporting] = useState(false);

  const tabParam = searchParams.get("tab") as FilterTab | null;
  const tab: FilterTab = tabParam && VALID_TABS.has(tabParam) ? tabParam : "all";
  const search = searchParams.get("search") ?? "";
  const sort = searchParams.get("sort") ?? "newest";
  const page = Number(searchParams.get("page") ?? "1") || 1;

  const setTab = (next: FilterTab) => {
    const p = new URLSearchParams(searchParams);
    next === "all" ? p.delete("tab") : p.set("tab", next);
    p.delete("page");
    setSearchParams(p);
  };

  const { data, isLoading } = useSales({
    page,
    pageSize: PAGE_SIZE,
    search: search.trim() || undefined,
    ...tabToQuery(tab),
    ...sortToQuery(sort),
  });

  const { data: stats, isLoading: statsLoading } = useAdminSalesStats();

  const orders = (data?.data ?? []).filter((row) => statusMatchesTab(tab, deriveOrderStatus(row)));
  const pageCount = data?.meta?.pageCount ?? 1;

  async function handleExportCsv() {
    setIsExporting(true);
    try {
      const res = await salesService.exportSalesCsv(
        {
          search: search.trim() || undefined,
          ...tabToQuery(tab),
          ...sortToQuery(sort),
        },
        { responseType: "blob" },
      );
      const blob = new Blob([res.data as unknown as BlobPart], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `orders-export-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      showErrorToast("Failed to export orders. Please try again.", error);
    } finally {
      setIsExporting(false);
    }
  }

  return (
    <div className="flex min-h-full w-full flex-col gap-6 bg-bgLight px-4 py-6 md:px-8 md:py-8">
      <div className="flex items-center justify-between gap-3">
        <Heading variant="h2" tone="dark">Orders</Heading>
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<IconDownload size={16} />}
          onClick={handleExportCsv}
          disabled={isExporting}
        >
          {isExporting ? "Exporting…" : "Export CSV"}
        </Button>
      </div>

      <div className="grid grid-cols-4 gap-4 max-md:grid-cols-2 max-md:gap-3">
        <AccentStatCard
          accent="green"
          label="Total Orders"
          value={(stats?.totalOrders ?? 0).toLocaleString()}
        />
        <AccentStatCard
          accent="cyan"
          label="In Transit"
          value={(stats?.inTransitOrders ?? 0).toLocaleString()}
        />
        <AccentStatCard
          accent="amber"
          label="Pending Delivery Approval"
          value={(stats?.pendingApprovalOrders ?? 0).toLocaleString()}
        />
        <AccentStatCard
          accent="amber"
          label="Unrouted Orders"
          value={(stats?.unroutedOrders ?? 0).toLocaleString()}
        />
      </div>

      <div className="flex items-center gap-4 max-md:flex-wrap max-md:gap-3">
        <SegmentedControl
          options={FILTER_TABS}
          value={tab}
          onChange={setTab}
          aria-label="Filter orders by status"
        />

        <div className="flex-1 max-md:basis-full">
          <SearchBar
            value={search}
            onChange={(e) => {
              const p = new URLSearchParams(searchParams);
              e.target.value.trim() ? p.set("search", e.target.value) : p.delete("search");
              p.delete("page");
              setSearchParams(p);
            }}
            placeholder="Search"
          />
        </div>

        <Select
          name="sort"
          value={sort}
          onChange={(e) => {
            const p = new URLSearchParams(searchParams);
            e.target.value === "newest" ? p.delete("sort") : p.set("sort", e.target.value);
            p.delete("page");
            setSearchParams(p);
          }}
          options={SORT_OPTIONS}
          density="default"
          fullWidth={false}
        />
      </div>

      {isLoading || statsLoading ? (
        <ShimmerLoader message="Fetching orders…">
          <div className="flex flex-col gap-2">
            {Array.from({ length: 7 }).map((_, i) => (
              <Shimmer key={i} className="h-14 w-full" />
            ))}
          </div>
        </ShimmerLoader>
      ) : orders.length === 0 ? (
        <div className="rounded-2xl border border-borderDefault bg-bgWhite">
          <EmptyState
            icon={IconShoppingCart}
            actionable
            title="No orders yet"
          />
        </div>
      ) : (
        <SimpleTable
          columns={COLUMNS}
          data={orders}
          keyExtractor={(o) => String(o.id)}
          onRowClick={(o) => navigate(AppRoutes.ADMIN_ORDER_DETAILS.replace(":id", String(o.id)))}
          footer={
            pageCount > 1 ? (
              <div className="flex justify-center">
                <Pagination
                  page={page}
                  pageCount={pageCount}
                  onPageChange={(next) => {
                    const p = new URLSearchParams(searchParams);
                    p.set("page", String(next));
                    setSearchParams(p, { replace: true });
                  }}
                />
              </div>
            ) : undefined
          }
        />
      )}
    </div>
  );
}
